import { useState, useEffect } from 'react';
import { Button } from '~/components/ui/Button';

const DISMISSED_KEY = 'ios-install-banner-dismissed';

function isIOS(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
}

function isStandalone(): boolean {
  return window.matchMedia('(display-mode: standalone)')
    .matches;
}

function ShareIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
      <polyline points="16 6 12 2 8 6" />
      <line x1="12" y1="2" x2="12" y2="15" />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

export function IOSInstallBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (
      isIOS() &&
      !isStandalone() &&
      !localStorage.getItem(DISMISSED_KEY)
    ) {
      setVisible(true);
    }
  }, []);

  function dismiss() {
    localStorage.setItem(DISMISSED_KEY, '1');
    setVisible(false);
  }

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 p-4"
      style={{
        paddingBottom:
          'calc(1rem + env(safe-area-inset-bottom))',
      }}
      data-testid="ios-install-banner"
    >
      <div className="bg-deep-blue-800 border border-deep-blue-600 rounded-xl p-4 shadow-2xl flex items-start gap-3">
        <ShareIcon className="w-5 h-5 text-neon-blue-400 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="font-pixel text-xs text-neon-blue-300 leading-relaxed">
            Install Choreboard
          </p>
          <p className="text-xs text-deep-blue-400 mt-1 leading-relaxed">
            Tap{' '}
            <ShareIcon className="inline w-3.5 h-3.5 text-deep-blue-400" />{' '}
            Share, then{' '}
            <strong className="text-neon-blue-200">
              Add to Home Screen
            </strong>
          </p>
        </div>
        <Button
          variant="unstyled"
          onClick={dismiss}
          className="text-deep-blue-400 hover:text-neon-blue-300 transition-colors shrink-0 -mt-0.5"
          aria-label="Dismiss install banner"
          data-testid="ios-install-banner-dismiss"
        >
          <XIcon className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
